import { useGlobalNotifier, useMessage } from "@vrf/fr-vrf-core-react";
import {
  FrVrfButton,
  FrVrfCheckboxesField,
  FrVrfColumnPanel,
  FrVrfDropdownField,
  FrVrfFooter,
} from "@vrf/fr-vrf-web-components-react";
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  RootCmp,
  addCmpProjectDialogInitialState,
  useAddCmpProjectDialogState,
} from ".";
import { ApplicationMessageScreen } from "../../../messages/screen-message";
import { usePageNavigation } from "../../../pages/use-page-navigation";
import {
  GetComponentResponse,
  useGetComponent,
} from "../../../services/component";
import { useCreateComponentProject } from "../../../services/component-project";
import {
  useGetDipByRegionList,
  useGetGeneralCodes,
} from "../../../services/data-list";
import { useGetPartners } from "../../../services/partners";
import { useGetRootComponents } from "../../../services/root-components";
import {
  CpntPjtType,
  InDefCommonFlagType,
} from "../../../util/general-code/enums/general-codes";
import { Option } from "../../../util/option";
import { createModal } from "../../modal";

export const AddCmpProjectDialogContent = () => {
  const { showConfirmationDialog, showToast, errorMessages, resetUserError } =
    useGlobalNotifier();
  const { messageForScreen } = useMessage();
  const search = useLocation().search;
  const queryParameter = new URLSearchParams(search);
  const { moveToModalPage, closeModalPage, isModalPage } = usePageNavigation();
  const [dialogState, setDialogState] = useAddCmpProjectDialogState();
  const [getGeneralCodesResponse, sendGeneralCodesRequest] =
    useGetGeneralCodes();
  const [getDipByRegionListResponse, sendGetDipByRegionListRequest] =
    useGetDipByRegionList();
  const [getRootComponentsResponse, sendGetRootComponentsRequest] =
    useGetRootComponents();
  const [getComponentResponse, sendGetComponentRequest] = useGetComponent();
  const [getPartnersResponse, sendGetPartnersRequest] = useGetPartners();
  const [getCreateComponentProjectResponse, sendCreateComponentProjectRequest] =
    useCreateComponentProject();
  const params = {
    godFlg: queryParameter.get("godFlg") ?? "",
  };
  const EVENT_CHANGE_PROJECT_TYPE = "projectType";
  const EVENT_CHANGE_DIP_BY_REGION = "dipByRegion";
  const EVENT_CHANGE_COMPONENT_ID = "componentId";
  const EVENT_CHANGE_PARTNER = "partner";
  const EVENT_CHANGE_PROTOCOL = "protocol";
  const EVENT_CHANGE_CHANGE_TARGET = "changeTarget";

  const onInitialDisplayed = () => {
    moveToModalPage();
    resetUserError();
    const cdTypes = ["cpnt_pjt_type"];
    sendGeneralCodesRequest({ cdTypes: cdTypes });
    sendGetDipByRegionListRequest({});
    sendGetRootComponentsRequest({});
    setDialogState((prevState) => ({
      ...prevState,
      projectType: "",
      dipByRegion: "",
      rootComponentId: "",
      componentId: "",
      protocol: "",
      changeTarget: [],
      partner: "",
      componentIdMap: new Map<string, any>(),
      filteredComponentIdList: [],
      protocolMap: new Map<string, any>(),
      filteredProtocolList: [],
      partnerList: [],
      isEdited: false,
    }));
  };

  const ongetGetGeneralCodesResponse = () => {
    let cpntPjtType = [] as Option[];
    const GeneralCode = getGeneralCodesResponse?.gnrlCdTypeList ?? [];
    if (GeneralCode.length === 0) {
      return;
    }
    GeneralCode.forEach((e) => {
      switch (e.cdType) {
        case "cpnt_pjt_type":
          cpntPjtType = e.cdList;
          break;
      }
    });
    setDialogState((prevState) => ({
      ...prevState,
      projectTypeList: cpntPjtType,
    }));
  };

  const ongetDipByRegionListResponse = () => {
    const res = getDipByRegionListResponse?.dipByRgnList ?? [];
    if (res.length === 0) {
      return;
    }
    const dipByRegionList = [] as Option[];
    res.forEach((e) => {
      const dipByRegion = {} as Option;
      dipByRegion.label = e.dipByRgn;
      dipByRegion.value = e.dipByRgn;
      dipByRegionList.push(dipByRegion);
    });
    setDialogState((prevState) => ({
      ...prevState,
      dipByRegionList: dipByRegionList,
    }));
  };

  const ongetRootComponentsResponse = () => {
    const res = getRootComponentsResponse?.rootCpntList ?? [];
    if (res.length === 0) {
      return;
    }
    const componentIdMap = new Map<string, RootCmp[]>();
    res.forEach((rCmp) => {
      const newRootComponentList: RootCmp[] = res.map((e) => {
        const cmpIdOption: Option[] = e.cpntList.map((cmp) => ({
          label: cmp.cpntId,
          value: cmp.cpntId,
        }));
        return {
          rootCmpId: e.rootCpntId,
          cmpIdOption: cmpIdOption,
        };
      });
      componentIdMap.set(rCmp.dipByRgn, newRootComponentList);
    });
    setDialogState((prevState) => ({
      ...prevState,
      componentIdMap: componentIdMap,
    }));
  };

  const ongetComponentResponse = () => {
    const res = getComponentResponse ?? ({} as GetComponentResponse);
    if (Object.keys(res).length === 0) {
      return;
    }
    const edhPrtcl = res.edhPrtclList;
    const edhNtfctnType = res.edhNtfctnTypeList;
    const protocolList = [] as Option[];
    if (dialogState.projectType === CpntPjtType.EDH) {
      edhPrtcl.forEach((e) => {
        const protocol = {} as Option;
        protocol.label = e.inOutTypeName + " " + e.edhPrtclTypeName;
        protocol.value = e.inOutTypeCd + "/" + e.edhPrtclTypeCd;
        protocolList.push(protocol);
      });
    } else if (dialogState.projectType === CpntPjtType.NOTIFICATOR) {
      edhNtfctnType.forEach((e) => {
        const protocol = {} as Option;
        protocol.label = e.inOutTypeName + " " + e.edhNtfctnTypeName;
        protocol.value = e.inOutTypeCd + "/" + e.edhNtfctnTypeCd;
        protocolList.push(protocol);
      });
    }
    setDialogState((prevState) => ({
      ...prevState,
      filteredProtocolList: protocolList,
    }));
  };

  const ongetPartnersResponse = () => {
    const res = getPartnersResponse?.ptnrList ?? [];
    if (res.length === 0) {
      return;
    }
    const partnerList = [] as Option[];
    const protocolMap = new Map<string, Option[]>();
    res.forEach((ptnr) => {
      const partner = {} as Option;
      partner.label = ptnr.ptnrName;
      partner.value = ptnr.ptnrId;
      partnerList.push(partner);
      const protocolList = [] as Option[];
      ptnr.chnTypeList.forEach((chnType) => {
        const protocol = {} as Option;
        protocol.label = chnType.inOutTypeName + " " + chnType.chnTypeName;
        protocol.value = chnType.inOutTypeCd + "/" + chnType.chnTypeCd;
        if (
          !protocolList.some(
            (e) => e.label === protocol.label && e.value === protocol.value,
          )
        ) {
          protocolList.push(protocol);
        }
      });
      protocolMap.set(ptnr.ptnrId, protocolList);
    });
    setDialogState((prevState) => ({
      ...prevState,
      partnerList: partnerList,
      protocolMap: protocolMap,
    }));
  };

  const ongetCreateComponentProjectResponse = () => {
    if (getCreateComponentProjectResponse) {
      showToast({
        design: "completion",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
      });
      closeModalPage();
      setDialogState(addCmpProjectDialogInitialState);
    }
  };

  useEffect(onInitialDisplayed, []);
  useEffect(ongetGetGeneralCodesResponse, [getGeneralCodesResponse]);
  useEffect(ongetDipByRegionListResponse, [getDipByRegionListResponse]);
  useEffect(ongetRootComponentsResponse, [getRootComponentsResponse]);
  useEffect(ongetComponentResponse, [getComponentResponse]);
  useEffect(ongetPartnersResponse, [getPartnersResponse]);
  useEffect(ongetCreateComponentProjectResponse, [
    getCreateComponentProjectResponse,
  ]);

  const onChangeValueHandle = (name: string) => (event: CustomEvent) => {
    const inputValue = event.detail.value;
    if (name === EVENT_CHANGE_PROJECT_TYPE) {
      setDialogState((prevState) => ({
        ...prevState,
        projectType: inputValue,
        dipByRegion: "",
        componentId: "",
        partner: "",
        protocol: "",
        filteredComponentIdList: [],
        partnerList: [],
        filteredProtocolList: [],
        isEdited: true,
      }));
      return;
    }
    if (name === EVENT_CHANGE_DIP_BY_REGION) {
      const rootComponentIdList =
        dialogState.componentIdMap.get(inputValue) ?? [];
      const componentIdList = rootComponentIdList.flatMap((e) => e.cmpIdOption);

      const deduplicatedComponentIdList = componentIdList.filter(
        (e, index, self) =>
          self.findIndex((f) => f.value === e.value) === index,
      );
      setDialogState((prevState) => ({
        ...prevState,
        rootComponentId: "",
        dipByRegion: inputValue,
        componentId: "",
        partner: "",
        protocol: "",
        filteredComponentIdList: deduplicatedComponentIdList,
        partnerList: [],
        filteredProtocolList: [],
        isEdited: true,
      }));
      return;
    }
    if (name === EVENT_CHANGE_COMPONENT_ID) {
      if (
        dialogState.projectType === CpntPjtType.EDH ||
        dialogState.projectType === CpntPjtType.NOTIFICATOR
      ) {
        const rootComponentId = dialogState.componentIdMap
          .get(dialogState.dipByRegion)
          ?.find(
            (e) => !!e.cmpIdOption.find((f) => f.value === inputValue),
          )?.rootCmpId;
        if (!rootComponentId) return;
        resetUserError();
        sendGetComponentRequest({
          dipByRgn: dialogState.dipByRegion,
          rootComponentId: rootComponentId,
          componentId: inputValue,
          godFlg: params.godFlg,
        });
      } else {
        resetUserError();
        sendGetPartnersRequest({
          dipByRgn: dialogState.dipByRegion,
          cpntId: inputValue,
        });
      }
      setDialogState((prevState) => ({
        ...prevState,
        componentId: inputValue,
        partner: "",
        protocol: "",
        partnerList: [],
        filteredProtocolList: [],
        isEdited: true,
      }));
      return;
    }
    if (name === EVENT_CHANGE_PARTNER) {
      const protocolList = dialogState.protocolMap.get(inputValue) ?? [];
      setDialogState((prevState) => ({
        ...prevState,
        partner: inputValue,
        protocol: "",
        filteredProtocolList: protocolList,
      }));
      return;
    }
    if (name === EVENT_CHANGE_PROTOCOL) {
      setDialogState((prevState) => ({
        ...prevState,
        protocol: inputValue,
        isEdited: true,
      }));
      return;
    }
    if (name === EVENT_CHANGE_CHANGE_TARGET) {
      setDialogState((prevState) => ({
        ...prevState,
        changeTarget: inputValue,
        isEdited: true,
      }));
      return;
    }
  };

  const onClickAddCmpPjt = () => {
    showConfirmationDialog({
      type: "confirm",
      title: "",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00045),
      onOkButtonClicked: () => {
        resetUserError();
        const protocol = dialogState.protocol.split("/");
        const inOutTypeCd = protocol[0];
        const prtclTypeCd = protocol[1];
        const changeAttributeFlg = dialogState.changeTarget.includes(
          "Attribute",
        )
          ? InDefCommonFlagType.YES
          : InDefCommonFlagType.NO;
        const changeInfraFlg = dialogState.changeTarget.includes(
          "Infrastructure",
        )
          ? InDefCommonFlagType.YES
          : InDefCommonFlagType.NO;
        sendCreateComponentProjectRequest({
          cpntPjtTypeCd: dialogState.projectType,
          dipByRgn: dialogState.dipByRegion,
          cpntId: dialogState.componentId,
          ptnrId: dialogState.partner ?? "",
          inOutTypeCd: inOutTypeCd,
          prtclTypeCd: prtclTypeCd,
          deletePjtFlg: InDefCommonFlagType.NO,
          stgOnlyFlg: InDefCommonFlagType.NO,
          constructWithOtherStgFlg: InDefCommonFlagType.NO,
          changeAttributeFlg: changeAttributeFlg,
          changeInfraFlg: changeInfraFlg,
        });
      },
    });
  };

  const onClickCancel = () => {
    if (!dialogState.isEdited) {
      closeModalPage();
      setDialogState((prevState) => ({
        ...prevState,
        isShow: false,
        isEdited: false,
      }));
      return;
    }
    showConfirmationDialog({
      type: "confirm",
      title: "",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
      onOkButtonClicked: () => {
        closeModalPage();
        setDialogState((prevState) => ({
          ...prevState,
          isShow: false,
          isEdited: false,
        }));
      },
    });
  };

  return (
    <>
      {createModal(
        "Add Cmp Project",
        <div>
          <FrVrfColumnPanel>
            <FrVrfDropdownField
              labelContent="Project Type"
              selectedValue={dialogState.projectType}
              onValueChanged={onChangeValueHandle("projectType")}
              items={dialogState.projectTypeList}
              errorMessage={errorMessages(
                dialogState.fieldPath.cpntPjtTypeCd,
                isModalPage(),
              )}
            />
            <FrVrfDropdownField
              labelContent="DIP by Region"
              selectedValue={dialogState.dipByRegion}
              onValueChanged={onChangeValueHandle("dipByRegion")}
              items={dialogState.dipByRegionList}
              errorMessage={errorMessages(
                dialogState.fieldPath.dipByRgn,
                isModalPage(),
              )}
            />
            <FrVrfDropdownField
              labelContent="Component ID"
              selectedValue={dialogState.componentId}
              onValueChanged={onChangeValueHandle("componentId")}
              items={dialogState.filteredComponentIdList}
              errorMessage={errorMessages(
                dialogState.fieldPath.cpntId,
                isModalPage(),
              )}
            />
            {dialogState.projectType === CpntPjtType.EDI && (
              <FrVrfDropdownField
                labelContent="Partner(for only EDI)"
                selectedValue={dialogState.partner}
                onValueChanged={onChangeValueHandle("partner")}
                items={dialogState.partnerList}
                errorMessage={errorMessages(
                  dialogState.fieldPath.ptnrId,
                  isModalPage(),
                )}
              />
            )}
            <FrVrfDropdownField
              labelContent="Protocol"
              selectedValue={dialogState.protocol}
              onValueChanged={onChangeValueHandle("protocol")}
              items={dialogState.filteredProtocolList}
              errorMessage={errorMessages(
                dialogState.fieldPath.prtclTypeCd,
                isModalPage(),
              )}
            />
            <FrVrfCheckboxesField
              labelContent="Change Target"
              options={dialogState.changeTargetOption}
              checkList={dialogState.changeTarget}
              onCheckboxesChanged={onChangeValueHandle("changeTarget")}
              errorMessage={errorMessages(
                dialogState.fieldPath.changeAttributeFlg,
                isModalPage(),
              )}
            />
          </FrVrfColumnPanel>
          <FrVrfFooter>
            <>
              <FrVrfButton
                content="Cancel"
                theme="secondary"
                size="large"
                onClicked={onClickCancel}
              />
              <FrVrfButton
                content="Add Cmp Project"
                theme="primary"
                size="large"
                disabled={!dialogState.isEdited}
                onClicked={onClickAddCmpPjt}
              />
            </>
          </FrVrfFooter>
        </div>,
        () => {},
        "medium",
      )}
    </>
  );
};
